import { Save } from '@mui/icons-material';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
} from '@mui/material';
import React, { useCallback, useState } from 'react';
import { DialogTitleWithCloseIcon } from 'src/components/organisms/DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';
import { useFurnitureTypeQuery } from 'src/hooks/inventory-master-data/queries/useFurnitureTypesQuery';
import { useInventoryMasterDataSaveChanges } from 'src/hooks/inventory-master-data/save-changes/useInventoryMasterDataSaveChanges';
import { useInventoryMasterDataUpdateChanges } from 'src/hooks/inventory-master-data/save-changes/useInventoryMasterDataUpdateChanges';
import { InventoryMasterDataRow } from 'src/hooks/inventory-master-data/table/useInventoryMasterDataRows';
import { useDiscardShortcut } from 'src/hooks/shortcuts/useDiscardShortcut';
import { useSaveShortcut } from 'src/hooks/shortcuts/useSaveShortcut';

interface AddFurnitureDialogProps {
    isOpen: boolean;
    close: () => void;
    furnitureRow?: InventoryMasterDataRow | null;
    nextSortOrder: number;
}

interface AddFurnitureData {
    name: string;
    description: string;
    furnitureTypeId: number | undefined;
}

type FurnitureData = Partial<Pick<InventoryMasterDataRow, 'id'>> & Omit<InventoryMasterDataRow, 'id' | 'typeName'>;

export const AddFurnitureDialog: React.FC<AddFurnitureDialogProps> = ({
    isOpen,
    close,
    furnitureRow,
    nextSortOrder,
}) => {
    const { data: furnitureTypes, loading: furnitureTypesLoading } = useFurnitureTypeQuery();
    const [saveFurniture, { loading: savingFurniture }] = useInventoryMasterDataSaveChanges();
    const [updateFurniture, { loading: updatingFurniture }] = useInventoryMasterDataUpdateChanges();
    const [furnitureData, setFurnitureData] = useState<AddFurnitureData>(
        furnitureRow ?? { name: '', description: '', furnitureTypeId: furnitureTypes?.[0].id }
    );

    const handleChangeType = (event: SelectChangeEvent) => {
        setFurnitureData((prev) => ({ ...prev, furnitureTypeId: Number(event.target.value) }));
    };

    const handleInputChange = (field: keyof FurnitureData) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setFurnitureData((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleSave = async () => {
        if (!furnitureData.furnitureTypeId) return;
        if (furnitureRow?.id) {
            const updatePayload = {
                id: furnitureRow.id,
                name: furnitureData.name,
                description: furnitureData.description,
                furnitureTypeId: furnitureData.furnitureTypeId,
                sortOrder: furnitureRow.sortOrder,
            };
            await updateFurniture(updatePayload);
        } else {
            const createPayload = {
                name: furnitureData.name,
                description: furnitureData.description,
                furnitureTypeId: furnitureData.furnitureTypeId,
                sortOrder: nextSortOrder,
            };
            await saveFurniture(createPayload);
        }
        close();
    };

    const reset = useCallback(() => {
        setFurnitureData(furnitureRow ?? { name: '', description: '', furnitureTypeId: furnitureTypes?.[0].id });
    }, [furnitureRow, furnitureTypes]);

    useSaveShortcut(handleSave, false);
    useDiscardShortcut(reset, false);

    return (
        <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={close}>
            <DialogTitleWithCloseIcon
                title={furnitureRow ? 'Edit Furniture' : 'Add New Furniture'}
                handleClose={close}
            />
            {furnitureTypes?.length === 0 || furnitureTypesLoading ? (
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            ) : (
                <DialogContent>
                    <Stack spacing={2}>
                        <TextField
                            id="name"
                            value={furnitureData.name}
                            label="Name"
                            variant="outlined"
                            onChange={handleInputChange('name')}
                        />
                        <TextField
                            id="description"
                            label="Description"
                            variant="outlined"
                            value={furnitureData.description}
                            onChange={handleInputChange('description')}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="type-select-label">Type</InputLabel>
                            <Select
                                labelId="type-select-label"
                                id="type-select"
                                value={furnitureData.furnitureTypeId?.toString() || ''}
                                label="Type"
                                onChange={handleChangeType}
                            >
                                {furnitureTypes?.map((type) => (
                                    <MenuItem key={type.id} value={type.id.toString()}>
                                        {type.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </DialogContent>
            )}

            <DialogActions>
                <Button variant="text" onClick={close}>
                    Cancel
                </Button>
                <Button
                    color="info"
                    loading={savingFurniture || updatingFurniture}
                    loadingPosition="start"
                    startIcon={<Save />}
                    variant="contained"
                    type="submit"
                    onClick={handleSave}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};
